import clsx from 'clsx'
import { useState } from 'react'

import {
  Dialog,
  DialogContent,
  DialogOverlay,
  type DialogProps,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { Button } from '@wartek-id/button-v1'

import type { UnorPegawai } from 'types/kinerja'
import type { PanelListItemProps } from './UnorCard'

interface DialogKonfirmasiProps extends Omit<DialogProps, 'children'> {
  isSatuanPendidikanIndukCorrect?: string | null
  isAtasanCorrect?: string | null
  data: UnorPegawai
  onConfirm: () => void
}

function DialogPanelListItem({
  label,
  value,
  testId,
  className,
  ...props
}: PanelListItemProps) {
  return (
    <div className={clsx('flex gap-2', className)} {...props}>
      <Text variant="body" className="mb-1 w-48">
        {label}
      </Text>
      <Text data-testid={testId} className="ml-auto">
        {value}
      </Text>
    </div>
  )
}

const CONFIRMATION_TEXT = 'Saya setuju untuk lanjut dengan data ini.'

export default function DialogKonfirmasi({
  isSatuanPendidikanIndukCorrect = null,
  isAtasanCorrect = null,
  data,
  onConfirm,
  ...dialogProps
}: DialogKonfirmasiProps) {
  const [confirmationText, setConfirmationText] = useState('')
  const isConfirmDisabled = confirmationText !== CONFIRMATION_TEXT

  return (
    <Dialog {...dialogProps}>
      <DialogOverlay />

      <DialogContent>
        <div className="p-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <Text variant="display-sm">Lanjut dengan data ini?</Text>

            <Text color="subdued">
              Data akan masuk ke dalam proses pengaliran data ke E-Kinerja.
              Pastikan Data Satuan Pendidikan Induk sudah sesuai agar data tidak
              dialirkan ke satuan pendidikan yang berbeda.
            </Text>

            <div
              className={clsx(
                'rounded p-4 flex flex-col gap-3',
                isSatuanPendidikanIndukCorrect === 'true'
                  ? 'bg-green-5'
                  : 'bg-red-5'
              )}
            >
              <div className="flex items-center gap-2">
                <Icon>check_circle_outline</Icon>
                <Text variant="body-bold">
                  Data Satuan Pendidikan Induk{' '}
                  {isSatuanPendidikanIndukCorrect === 'true'
                    ? 'benar'
                    : 'salah'}
                </Text>
              </div>
              <DialogPanelListItem
                label="Nama Unit Kerja"
                value={data.satuanPendidikan}
              />
              <DialogPanelListItem
                label="Unit Kerja ID"
                value={data.unorIdPegawai}
              />
            </div>

            <div
              className={clsx(
                'rounded p-4 flex flex-col gap-3',
                isAtasanCorrect === 'true' ? 'bg-green-5' : 'bg-red-5'
              )}
            >
              <div className="flex items-center gap-2">
                <Icon>check_circle_outline</Icon>
                <Text variant="body-bold">
                  Data Atasan {isAtasanCorrect === 'true' ? 'benar' : 'salah'}
                </Text>
              </div>
              {data.namaAtasan === '' ? (
                <Text>Tidak ada data.</Text>
              ) : (
                <>
                  <DialogPanelListItem label="Nama" value={data.namaAtasan} />
                  <DialogPanelListItem label="NIP" value={data.nipAtasan} />
                  <DialogPanelListItem
                    label="Status"
                    value={data.statusAtasan}
                  />
                </>
              )}
            </div>
          </div>

          <Text variant="body-lg" color="subdued">
            Mohon ketik kalimat ini pada kolum kalimat persetujuan:
          </Text>

          <div className="rounded p-2 bg-blue-5">
            <Text variant="heading-sm" className="select-none !text-blue-60">
              {CONFIRMATION_TEXT}
            </Text>
          </div>

          <div className="flex flex-col gap-1">
            <Text>Kalimat persetujuan</Text>
            <Textarea
              placeholder="Ketik di sini."
              value={confirmationText}
              onChange={(e) => {
                setConfirmationText(e.target.value)
              }}
            />
          </div>

          <div className="flex gap-4">
            <Button
              size="lg"
              color="white"
              onClick={() => dialogProps?.onClose()}
            >
              Kembali
            </Button>
            <Button
              size="lg"
              color="blue"
              disabled={isConfirmDisabled}
              onClick={onConfirm}
            >
              Konfirmasi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
